import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Search, Filter, Check, X, Edit2, Trash2, Loader2,
  CheckCircle, XCircle, Clock, Award, BarChart3, RefreshCw,
  ChevronDown, Eye, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const API = `${API_BASE}/api`;

// Competency Categories
const COMPETENCY_CATEGORIES = [
  "Technical Competence",
  "Regulatory & Compliance Competence",
  "Digital & Technology Competence",
  "Critical Thinking & Problem Solving",
  "Professional Judgement",
  "Communication & Stakeholder Engagement",
  "Documentation & Evidence Management",
  "Ethics & Professional Conduct",
  "Behavioural & Workplace Effectiveness",
  "Leadership & Strategic Capability"
];

// Target Levels
const TARGET_LEVELS = ["Foundation", "Applied", "Proficient", "Advanced", "Strategic"];

// Status colors
const STATUS_COLORS = {
  pending: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  approved: "bg-green-500/20 text-green-400 border-green-500/30",
  rejected: "bg-red-500/20 text-red-400 border-red-500/30"
};

// Category colors
const CATEGORY_COLORS = {
  "Technical Competence": "bg-blue-500/20 text-blue-400",
  "Regulatory & Compliance Competence": "bg-purple-500/20 text-purple-400",
  "Digital & Technology Competence": "bg-cyan-500/20 text-cyan-400",
  "Critical Thinking & Problem Solving": "bg-amber-500/20 text-amber-400",
  "Professional Judgement": "bg-emerald-500/20 text-emerald-400",
  "Communication & Stakeholder Engagement": "bg-pink-500/20 text-pink-400",
  "Documentation & Evidence Management": "bg-slate-500/20 text-slate-400",
  "Ethics & Professional Conduct": "bg-indigo-500/20 text-indigo-400",
  "Behavioural & Workplace Effectiveness": "bg-orange-500/20 text-orange-400",
  "Leadership & Strategic Capability": "bg-rose-500/20 text-rose-400"
};

export default function CompetenciesAdmin() {
  const { token } = useAuth();
  const [competencies, setCompetencies] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompetency, setEditingCompetency] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingCompetency, setViewingCompetency] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    fetchCompetencies();
  }, []);
  
  const fetchCompetencies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/competencies/library/all`, { headers });
      setCompetencies(response.data.competencies || []);
      setCounts(response.data.counts || { total: 0, pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Failed to fetch competencies:', error);
      toast.error('Failed to load competencies');
    } finally {
      setLoading(false);
    }
  };
  
  const handleStatusChange = async (competencyId, newStatus) => {
    try {
      await axios.put(`${API}/competencies/library/${competencyId}/status`, 
        { status: newStatus }, 
        { headers }
      );
      
      // Update local state
      setCompetencies(prev => prev.map(c => 
        c.competency_id === competencyId ? { ...c, status: newStatus } : c
      ));
      
      // Update counts
      setCounts(prev => {
        const oldStatus = competencies.find(c => c.competency_id === competencyId)?.status;
        return {
          ...prev,
          [oldStatus]: Math.max(0, prev[oldStatus] - 1),
          [newStatus]: prev[newStatus] + 1
        };
      });
      
      toast.success(`Competency ${newStatus}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };
  
  const handleEdit = (competency) => {
    setEditingCompetency({ ...competency });
    setEditDialogOpen(true);
  };
  
  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      await axios.put(
        `${API}/competencies/library/${editingCompetency.competency_id}`,
        { competency: editingCompetency },
        { headers }
      );
      
      setCompetencies(prev => prev.map(c => 
        c.competency_id === editingCompetency.competency_id ? editingCompetency : c
      ));
      
      setEditDialogOpen(false);
      setEditingCompetency(null);
      toast.success('Competency updated');
    } catch (error) {
      console.error('Failed to update competency:', error);
      toast.error('Failed to update competency');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDelete = async (competencyId) => {
    if (!window.confirm('Are you sure you want to delete this competency?')) return;
    
    try {
      await axios.delete(`${API}/competencies/library/${competencyId}`, { headers });
      setCompetencies(prev => prev.filter(c => c.competency_id !== competencyId));
      toast.success('Competency deleted');
      
      // Update counts
      fetchCompetencies();
    } catch (error) {
      console.error('Failed to delete competency:', error);
      toast.error('Failed to delete competency');
    }
  };
  
  const handleView = (competency) => {
    setViewingCompetency(competency);
    setViewDialogOpen(true);
  };
  
  // Filtered competencies
  const filteredCompetencies = competencies.filter(c => {
    const matchesSearch = !searchQuery || 
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.category?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || c.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Competency Library</h2>
          <p className="text-zinc-400">Manage the master competency library</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchCompetencies}
          className="border-zinc-700"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-zinc-700 rounded-lg">
              <BarChart3 className="h-5 w-5 text-zinc-300" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{counts.total}</p>
              <p className="text-sm text-zinc-400">Total</p>
            </div>
          </div>
        </div>
        
        <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Clock className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{counts.pending}</p>
              <p className="text-sm text-zinc-400">Pending</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">{counts.approved}</p>
              <p className="text-sm text-zinc-400">Approved</p>
            </div>
          </div>
        </div>
        
        <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">{counts.rejected}</p>
              <p className="text-sm text-zinc-400">Rejected</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            placeholder="Search competencies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-800/50 border-zinc-700 text-white"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-zinc-800/50 border-zinc-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="all" className="text-white">All Status</SelectItem>
            <SelectItem value="pending" className="text-white">Pending</SelectItem>
            <SelectItem value="approved" className="text-white">Approved</SelectItem>
            <SelectItem value="rejected" className="text-white">Rejected</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-64 bg-zinc-800/50 border-zinc-700 text-white">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700 max-h-80">
            <SelectItem value="all" className="text-white">All Categories</SelectItem>
            {COMPETENCY_CATEGORIES.map((cat) => (
              <SelectItem key={cat} value={cat} className="text-white">
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Competencies Table */}
      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
          </div>
        ) : filteredCompetencies.length === 0 ? (
          <div className="text-center py-12">
            <Award className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No competencies found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-4 text-sm font-medium text-zinc-400">Competency</th>
                  <th className="text-left p-4 text-sm font-medium text-zinc-400">Category</th>
                  <th className="text-left p-4 text-sm font-medium text-zinc-400">Level</th>
                  <th className="text-left p-4 text-sm font-medium text-zinc-400">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-zinc-400">Usage</th>
                  <th className="text-right p-4 text-sm font-medium text-zinc-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompetencies.map((competency) => (
                  <tr 
                    key={competency.competency_id} 
                    className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                  >
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-white">{competency.name}</p>
                        <p className="text-sm text-zinc-500 line-clamp-1">{competency.definition}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={`text-xs ${CATEGORY_COLORS[competency.category] || 'bg-zinc-500/20'}`}>
                        {competency.category?.split(' ')[0]}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-zinc-300">{competency.target_level}</span>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className={STATUS_COLORS[competency.status]}>
                        {competency.status}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-zinc-400">{competency.usage_count || 0}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        {competency.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStatusChange(competency.competency_id, 'approved')}
                              className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStatusChange(competency.competency_id, 'rejected')}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleView(competency)}
                          className="text-zinc-400 hover:text-white"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(competency)}
                          className="text-zinc-400 hover:text-white"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(competency.competency_id)}
                          className="text-zinc-400 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{viewingCompetency?.name}</DialogTitle>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={CATEGORY_COLORS[viewingCompetency?.category]}>
                {viewingCompetency?.category}
              </Badge>
              <Badge variant="outline" className={STATUS_COLORS[viewingCompetency?.status]}>
                {viewingCompetency?.status}
              </Badge>
            </div>
          </DialogHeader>
          
          {viewingCompetency && (
            <div className="space-y-4 py-4">
              <div>
                <p className="text-zinc-500 text-sm mb-1">Definition</p>
                <p className="text-zinc-300">{viewingCompetency.definition}</p>
              </div>
              
              <div>
                <p className="text-zinc-500 text-sm mb-1">Measurable Outcome</p>
                <p className="text-zinc-300">{viewingCompetency.measurable_outcome}</p>
              </div>
              
              <div>
                <p className="text-zinc-500 text-sm mb-1">Target Level</p>
                <p className="text-zinc-300">{viewingCompetency.target_level}</p>
              </div>
              
              <div>
                <p className="text-zinc-500 text-sm mb-1">Skills Required</p>
                <div className="flex flex-wrap gap-2">
                  {(viewingCompetency.skills_required || []).map((skill, i) => (
                    <Badge key={i} variant="outline" className="border-zinc-600">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-zinc-500 text-sm mb-1">Evidence of Competence</p>
                <p className="text-zinc-300">{viewingCompetency.evidence_of_competence}</p>
              </div>
              
              <div className="pt-4 border-t border-zinc-800 text-sm text-zinc-500">
                <p>Created by: {viewingCompetency.created_by || 'System'}</p>
                <p>Usage count: {viewingCompetency.usage_count || 0}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-zinc-700">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Competency</DialogTitle>
          </DialogHeader>
          
          {editingCompetency && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-300">Name</Label>
                <Input
                  value={editingCompetency.name}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Category</Label>
                <Select
                  value={editingCompetency.category}
                  onValueChange={(value) => setEditingCompetency({ ...editingCompetency, category: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {COMPETENCY_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat} className="text-white">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Target Level</Label>
                <Select
                  value={editingCompetency.target_level}
                  onValueChange={(value) => setEditingCompetency({ ...editingCompetency, target_level: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {TARGET_LEVELS.map((level) => (
                      <SelectItem key={level} value={level} className="text-white">
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Status</Label>
                <Select
                  value={editingCompetency.status}
                  onValueChange={(value) => setEditingCompetency({ ...editingCompetency, status: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="pending" className="text-white">Pending</SelectItem>
                    <SelectItem value="approved" className="text-white">Approved</SelectItem>
                    <SelectItem value="rejected" className="text-white">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Definition</Label>
                <Textarea
                  value={editingCompetency.definition}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, definition: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Measurable Outcome</Label>
                <Textarea
                  value={editingCompetency.measurable_outcome}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, measurable_outcome: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Skills Required (comma-separated)</Label>
                <Input
                  value={(editingCompetency.skills_required || []).join(', ')}
                  onChange={(e) => setEditingCompetency({ 
                    ...editingCompetency, 
                    skills_required: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Evidence of Competence</Label>
                <Textarea
                  value={editingCompetency.evidence_of_competence}
                  onChange={(e) => setEditingCompetency({ ...editingCompetency, evidence_of_competence: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSaving} className="bg-teal-500 hover:bg-teal-600">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
